"use client";

import dynamic from "next/dynamic";
import type { KundaliLang } from "./KundaliFormCard.i18n";

const DailyInsight = dynamic(() => import("@/components/DailyInsight"), { ssr: false });

export function FormErrorMessage({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      className="text-[13px] rounded-lg p-3.5"
      style={{
        color: "var(--color-error-text)",
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      {error}
    </div>
  );
}

/**
 * User-facing retry card — shown when the kundali API is unreachable after all
 * attempts (a network fault, not a bad input). The 2026 resilience standard
 * (AWS Builders' Library) is "never a dead end": one obvious primary action to
 * try again (rule 39 — a single CTA), reusing the SAME idempotency key so a
 * re-send can never double-create. Replaces the prior inert error STRING that
 * left the seeker stranded — the documented shape of the 5,150 unreachable
 * failures. Voice per rule 22 (cosmic framing, never "we couldn't help you").
 */
export function KundaliRetryCard({
  title,
  body,
  actionLabel,
  onRetry,
  loading,
}: {
  title: string;
  body: string;
  actionLabel: string;
  onRetry: () => void;
  loading: boolean;
}) {
  return (
    <div
      data-testid="kundali-retry-card"
      role="alert"
      className="rounded-lg p-4 text-[13px]"
      style={{
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      <p className="font-semibold mb-1" style={{ color: "var(--color-error-text)" }}>
        {title}
      </p>
      <p className="mb-3" style={{ color: "var(--ish-text-muted)" }}>
        {body}
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={loading}
        aria-disabled={loading}
        className="btn-sacred w-full font-semibold py-3 rounded-lg text-[14px] disabled:opacity-60"
      >
        {actionLabel}
      </button>
    </div>
  );
}

export function KundaliSubmitButton({
  loading,
  disabled = false,
  label,
  loadingLabel,
}: {
  loading: boolean;
  disabled?: boolean;
  label: string;
  loadingLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      aria-disabled={loading || disabled}
      className="btn-sacred w-full font-semibold py-3.5 rounded-lg text-[14px] disabled:opacity-60"
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2.5">
          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          {loadingLabel}
        </span>
      ) : (
        label
      )}
    </button>
  );
}

// DailyInsight rendered height is variable: py-6 outer + rounded-2xl card p-8
// with text content + ShareActions + PersonalizeInsight + CategoryLabel. Long
// English/Hindi daily text regularly exceeds the old 340px reserve, and because
// the insight is delayed it can become a late LCP/CLS source on tool-entry pages.
// Without a reserved container the slot injects ~340px of content 5 s after
// initial paint, shifting AIAstrologerCTA + SEOContent (large) down by that
// amount → field p75 CLS ≈ 1.0 on /free-kundali/ (the only hard-failing Core
// Web Vital on the site). Reserving the same floor from t=0 eliminates the
// shift: content loads into a pre-reserved space, nothing moves.
// Over-reserving by a few pixels = harmless whitespace; under-reserving = residual CLS.
const DAILY_INSIGHT_MIN_HEIGHT = 560;

export function DailyInsightSlot({
  show,
  ready,
  language,
}: {
  show: boolean;
  ready: boolean;
  language: KundaliLang;
}) {
  if (!show) return null;
  return (
    <>
      <div className="divider-warm mx-auto max-w-sm mb-2" />
      <div style={{ minHeight: DAILY_INSIGHT_MIN_HEIGHT }}>
        {ready && <DailyInsight language={language} />}
      </div>
      <div className="divider-warm mx-auto max-w-sm mt-2 mb-6" />
    </>
  );
}
